/**
 * A consulta da oportunidade na tela (#811): o horário entre o campo e a API,
 * e as opções que a tela oferece. Sem React, para o teste não precisar de tela.
 */

import type { ConsultaDaOportunidade, OpcoesDaConsulta } from '@cliqcrm/contracts';
import type { ConsultaNaLista } from './consultas-na-tela';
import { instantePorExtenso } from './marcar-no-sisac';

type Opcao = OpcoesDaConsulta['medicos'][number];

const doisDigitos = (numero: number) => String(numero).padStart(2, '0');

/**
 * O instante da API no formato do campo `datetime-local`, no relógio de quem
 * está na tela: `2026-10-20T17:30:00Z` em São Paulo vira `2026-10-20T14:30`.
 */
export function paraCampo(iso: string | null): string {
  if (!iso) return '';
  const data = new Date(iso);
  return (
    `${data.getFullYear()}-${doisDigitos(data.getMonth() + 1)}-${doisDigitos(data.getDate())}` +
    `T${doisDigitos(data.getHours())}:${doisDigitos(data.getMinutes())}`
  );
}

/** O valor do campo de volta a instante, no relógio de quem digitou. Vazio é sem horário. */
export function doCampo(texto: string): string | null {
  if (texto.trim() === '') return null;
  const data = new Date(texto);
  return Number.isNaN(data.getTime()) ? null : data.toISOString();
}

/**
 * As opções da lista, mais a que já estava gravada se ela saiu do SISAC.
 *
 * Sem isso, reabrir a consulta de um médico que deixou a clínica mostraria o
 * campo vazio, e salvar apagaria quem atendeu sem ninguém ter pedido.
 */
export function opcoesComAtual(opcoes: readonly Opcao[], atual: Opcao | null): Opcao[] {
  if (!atual || opcoes.some((opcao) => opcao.id === atual.id)) return [...opcoes];
  return [...opcoes, atual];
}

/** Já marcada no SISAC: a integração gravou os códigos do paciente e da agenda. */
export function consultaMarcada(consulta: ConsultaDaOportunidade): boolean {
  return consulta.agendamento !== null;
}

/** O id da consulta na lista: o CRM guarda uma por oportunidade. */
export const ID_DA_CONSULTA = 'consulta-da-oportunidade';

/** A consulta da API como a lista a mostra; vazia não entra na lista. */
export function consultasNaLista(consulta: ConsultaDaOportunidade): ConsultaNaLista[] {
  if (consultaEmBranco(consulta)) return [];
  return [
    {
      id: ID_DA_CONSULTA,
      quando: consulta.consulta_em ? instantePorExtenso(consulta.consulta_em) : null,
      medico: consulta.medico,
      especialidade: consulta.especialidade,
      codigoDoPaciente: consulta.agendamento?.codigo_do_paciente ?? null,
      codigoDaAgenda: consulta.agendamento?.codigo_da_agenda ?? null,
      marcada: consultaMarcada(consulta),
    },
  ];
}

/** A consulta sem nada escolhido nem agendado: a tela oferece escolher. */
export function consultaEmBranco(consulta: ConsultaDaOportunidade): boolean {
  return (
    consulta.medico === null &&
    consulta.especialidade === null &&
    consulta.consulta_em === null &&
    consulta.agendamento === null
  );
}
